import React, { useEffect, useMemo, useRef, useState } from "react";
import { LuLock } from "react-icons/lu";
import NotificationBadge, {
  ScopesNotificationBadge,
} from "../Notifications/NotificationBadge";

const Chevron = ({ expanded }) => (
  <svg
    width="10"
    height="7"
    viewBox="0 0 10 7"
    fill="none"
    aria-hidden="true"
    className={`ml-kit-6 inline-block transition-transform duration-300 ease-out ${
      expanded ? "rotate-180" : ""
    }`}
  >
    <path
      d="M1 1.5 5 5.5 9 1.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PortalMenu = ({
  groups = [],
  currentTab,
  selectedItemId,
  selectedActionId,
  onSelect,
}) => {
  const visibleGroups = groups.filter((group) => group.items?.length > 0);
  const currentGroupId = useMemo(() => {
    const matchingGroup = visibleGroups.find((group) =>
      group.items.some((item) => item.tab === currentTab)
    );

    return matchingGroup?.id || visibleGroups[0]?.id;
  }, [currentTab, visibleGroups]);
  const [activeGroupId, setActiveGroupId] = useState(currentGroupId);

  useEffect(() => {
    if (currentGroupId) {
      setActiveGroupId(currentGroupId);
    }
  }, [currentGroupId]);

  const activeGroup =
    visibleGroups.find((group) => group.id === activeGroupId) || visibleGroups[0];
  const groupButtonRefs = useRef({});

  // the header row scrolls sideways when it doesn't fit; keep the selected header in view
  useEffect(() => {
    let cancelled = false;
    // wait for the header font: widths change once it loads
    (document.fonts?.ready || Promise.resolve()).then(() => {
      if (cancelled) return;
      groupButtonRefs.current[activeGroup?.id]?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    });
    return () => {
      cancelled = true;
    };
  }, [activeGroup?.id]);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [collapsedExpandableItemId, setCollapsedExpandableItemId] = useState(null);

  const getItemSelected = (item) =>
    item.showActive !== false &&
    (item.id === selectedItemId ||
      (!selectedItemId && item.tab && item.tab === currentTab));

  useEffect(() => {
    const selectedExpandableItem = activeGroup?.items.find(
      (item) => getItemSelected(item) && item.inlineActions?.length > 0
    );

    if (selectedExpandableItem && selectedExpandableItem.id !== collapsedExpandableItemId) {
      setExpandedItemId(selectedExpandableItem.id);
    } else {
      setExpandedItemId(null);
    }
  }, [activeGroup, collapsedExpandableItemId, currentTab, selectedItemId]);

  const expandedItem = activeGroup?.items.find(
    (item) => item.id === expandedItemId && item.inlineActions?.length > 0
  );
  const handleGroupClick = (group) => {
    setActiveGroupId(group.id);
    setCollapsedExpandableItemId(null);

    const defaultItem =
      group.items?.find((item) => item.id === group.defaultItemId) || group.items?.[0];
    if (defaultItem) {
      onSelect?.(defaultItem);
    }
  };

  return (
    <div className="w-full bg-[var(--inyc-primary)] px-kit-16">
      {/* py + matching -my give the badges and focus rings room inside the scroll area */}
      <div className="-mx-kit-16 -my-kit-8 flex items-baseline gap-kit-24 overflow-x-auto whitespace-nowrap px-kit-16 py-kit-8 scroll-px-kit-16 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {visibleGroups.map((group) => (
          <button
            key={group.id}
            ref={(node) => {
              groupButtonRefs.current[group.id] = node;
            }}
            type="button"
            onClick={() => handleGroupClick(group)}
            className={`py-kit-2 text-kit-2xl font-['Primary-Bold'] leading-none lowercase tracking-[-0.035em] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-[1.5px] focus-visible:ring-[var(--inyc-primary-text-color)] focus-visible:ring-offset-[4px] focus-visible:ring-offset-[var(--inyc-primary)] ${
              activeGroup?.id === group.id
                ? "text-[var(--inyc-primary-text-color)]"
                : "text-[var(--inyc-primary-text-color)] opacity-35 hover:opacity-65"
            }`}
          >
            {group.label}
            <ScopesNotificationBadge
              scopes={(group.items || [])
                .flatMap((item) => item.notificationScopes || [item.notificationScope])
                .filter(Boolean)}
              className="ml-kit-6 align-middle -translate-y-[0.35rem]"
            />
          </button>
        ))}
      </div>

      {activeGroup && !activeGroup.hideItems && (
        <>
          <div className="mt-kit-16 flex flex-wrap items-baseline gap-x-kit-20 gap-y-kit-6">
            {activeGroup.items.map((item) => {
              const selected = getItemSelected(item);
              const inlineActions = item.inlineActions || [];
              const expanded = expandedItemId === item.id;

              const handleClick = () => {
                if (inlineActions.length > 0) {
                  if (expanded) {
                    setCollapsedExpandableItemId(item.id);
                    setExpandedItemId(null);
                  } else {
                    setCollapsedExpandableItemId(null);
                    setExpandedItemId(item.id);
                  }
                } else {
                  setCollapsedExpandableItemId(null);
                  setExpandedItemId(null);
                }

                onSelect?.(item);
              };

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={handleClick}
                  aria-expanded={inlineActions.length > 0 ? expanded : undefined}
                  className={`inline-flex items-center border-b-[2px] px-0 pb-kit-6 pt-kit-2 text-kit-md leading-none lowercase transition-all duration-200 focus-visible:outline-none focus-visible:ring-[1.5px] focus-visible:ring-[var(--inyc-primary-text-color)] focus-visible:ring-offset-[4px] focus-visible:ring-offset-[var(--inyc-primary)] ${
                    selected
                      ? "border-[var(--inyc-primary-text-color)] font-['Primary-Bold'] text-[var(--inyc-primary-text-color)]"
                      : item.locked
                      ? "border-transparent font-['Primary-Bold'] text-[var(--inyc-primary-text-color)] opacity-30 hover:opacity-45"
                      : "border-transparent font-['Primary-Bold'] text-[var(--inyc-primary-text-color)] opacity-55 hover:opacity-85"
                  }`}
                >
                  {/* locked: the account can't use this yet; tapping explains (portalcontext handles item.locked) */}
                  {item.locked && <LuLock size={13} aria-label="locked" className="mr-kit-4" />}
                  <span
                    style={{
                      fontFamily: "Primary-Bold",
                      fontWeight: selected ? 800 : 700,
                    }}
                  >
                    {item.label}
                  </span>
                  {item.notificationScope && (
                    <NotificationBadge
                      scope={item.notificationScope}
                      className="ml-kit-6"
                    />
                  )}
                  {item.notificationScopes && (
                    <ScopesNotificationBadge
                      scopes={item.notificationScopes}
                      className="ml-kit-6"
                    />
                  )}
                  {inlineActions.length > 0 && <Chevron expanded={expanded} />}
                </button>
              );
            })}
          </div>
          <div
            className={`grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-out ${
              expandedItem ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="min-h-0">
              <div className="flex flex-wrap gap-kit-8 pb-kit-2 pt-kit-16">
                {expandedItem?.inlineActions?.map((action, index) => (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() => onSelect?.(action)}
                    className={`inyc-btn-chip ${selectedActionId === action.id ? "is-active" : ""}`}
                    style={{ transitionDelay: `${index * 45}ms` }}
                  >
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
      <div className="inyc-divider-fade is-solid mt-kit-12" />
    </div>
  );
};

export default PortalMenu;
